import { useId } from 'react';
import type { OrbState } from '../lib/orbState';
import type { AudioLevelRef } from '../lib/useAudioLevels';

/**
 * The signature voice orb (design-language §6) — the hero of the Live page. An organic, fluid,
 * breathing form in brushed-brass foil.
 *
 * R18 orb-disturbance fix (Item 3): the previous implementation ran a per-frame
 * requestAnimationFrame loop that called `filterScaleRef.setAttribute('scale', …)` on the SVG
 * feDisplacementMap every ~30fps. Animating an SVG filter attribute imperatively forces full
 * filter re-rasterization on the main thread every frame — expensive enough to starve the
 * WavStreamPlayer AudioWorklet → mid-sentence disturbance in every message, including the static
 * greeting (confirming it was not LLM streaming latency).
 *
 * Fix: the rAF loop and ALL per-frame DOM/SVG-filter writes are removed entirely. The orb is now
 * driven exclusively by CSS keyframe animations (compositor-only: transform + opacity). No JS
 * executes per frame during audio playback. The orb still shows Gigi's presence with a gentle
 * breathe/pulse; it is no longer voice-amplitude-reactive (intentional — reintroduce cheaply
 * via transform/opacity only if the user confirms the disturbance is gone).
 *
 * `levelRef` prop is retained for API compat (VoiceDemo passes it; useAudioLevels keeps writing
 * it and mirroring to the store at ~15fps). The orb simply no longer reads from it.
 *
 * Accessible: honours prefers-reduced-motion (static, still beautiful); ARIA role+label
 * derived from the status prop (React render, not imperative updates — correct for a static orb).
 */
/** R29 X4 — accessible status word per choreography state (for the aria-label + sr-only live region). */
const ORB_STATE_LABEL: Record<OrbState, string> = {
  idle: 'idle',
  listening: 'listening',
  speaking: 'speaking',
  thinking: 'on hold',
};

export function VoiceOrb({
  status,
  orbState,
  size = 200,
  levelRef: _levelRef,
}: {
  status: string;
  /**
   * R29 X4 — the derived choreography state (design-language §6). Optional: when absent, it falls
   * back to a status-only mapping (idle vs listening) so existing call-sites still work. The parent
   * subscribes to the DERIVED string via a zustand selector, so the orb re-renders only when the
   * state changes (≈once per turn) — never per transcript chunk / audio frame (the R18 constraint).
   */
  orbState?: OrbState;
  size?: number;
  /**
   * Retained for API compat with VoiceDemo/useVoice callers. No longer consumed per-frame
   * by the orb — the rAF animation loop has been removed (R18 orb-disturbance fix).
   * useAudioLevels continues to write this ref and mirror to the store independently.
   */
  levelRef?: React.RefObject<AudioLevelRef>;
}) {
  const uid = useId().replace(/:/g, '');

  const connected = status === 'connected';
  // Fall back to a status-only state when the parent doesn't pass one (API compat).
  const state: OrbState = orbState ?? (connected ? 'listening' : 'idle');

  // R29 X4 — choreography per state, all compositor-only (transform/opacity CSS keyframes):
  //   listening → ripple rings expand outward (the orb "reaches" for the visitor's voice)
  //   speaking  → livelier pulse + the rotating specular shimmer reads as a waveform sheen
  //   thinking  → the core gently SPINS (the shimmer group) while the blob holds a calm breathe
  //   idle      → slow breathe, rings hidden
  const showRipple = state === 'listening' || state === 'speaking';
  const coreClass =
    state === 'speaking' ? 'orb-pulse' : state === 'idle' ? 'orb-breathe' : 'orb-pulse';
  // The core breathes gently in 'thinking'; the SPIN is applied to the shimmer group below.
  const ariaLabel = `Voice assistant — ${ORB_STATE_LABEL[state]}`;

  return (
    <div
      className="relative grid place-items-center"
      style={{ width: size, height: size }}
      role="img"
      aria-label={ariaLabel}
      data-orb-state={state}
      data-testid="voice-orb"
    >
      {/* Ripple rings — pure CSS animation when the orb is listening/speaking; hidden otherwise. */}
      <span
        className="orb-ripple pointer-events-none absolute rounded-full"
        style={{
          width: size * 0.64,
          height: size * 0.64,
          border: '1px solid var(--c-accent)',
          opacity: 0,
          animation: showRipple ? 'orb-ripple 2.6s var(--ease-out) 0s infinite' : 'none',
        }}
      />
      <span
        className="orb-ripple pointer-events-none absolute rounded-full"
        style={{
          width: size * 0.64,
          height: size * 0.64,
          border: '1px solid var(--c-accent)',
          opacity: 0,
          animation: showRipple ? 'orb-ripple 2.6s var(--ease-out) 1.1s infinite' : 'none',
        }}
      />

      {/*
       * Core blob. CSS-only: gentle breathe when idle; active states use the livelier orb-pulse
       * keyframe (still compositor-only transform). NO per-frame JS or SVG-filter writes.
       */}
      <div
        className={coreClass}
        style={{
          width: size * 0.62,
          height: size * 0.62,
        }}
      >
        <svg
          viewBox="0 0 120 120"
          width="100%"
          height="100%"
          role="presentation"
          aria-hidden="true"
        >
          <defs>
            <radialGradient id={`foil-${uid}`} cx="42%" cy="36%" r="72%">
              <stop offset="0%" stopColor="#f4e2ac" />
              <stop offset="34%" stopColor="#d8b667" />
              <stop offset="66%" stopColor="#b78f3f" />
              <stop offset="100%" stopColor="#7c5e2a" />
            </radialGradient>
            <radialGradient id={`sheen-${uid}`} cx="38%" cy="30%" r="40%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.75)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </radialGradient>
            {/*
             * Organic liquid edge — turbulence displaced. scale is STATIC (= 4).
             * The baseFrequency SVG-SMIL animate drives gentle edge drift via the browser's
             * own SVG animation path (not JS / not per-frame setAttribute) — the browser
             * schedules this on its own compositing timeline, not the main thread rAF queue.
             * This is the only filter work; it is not voice-reactive.
             */}
            <filter id={`liquid-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.012"
                numOctaves={2}
                seed={7}
                result="noise"
              >
                <animate
                  attributeName="baseFrequency"
                  dur="14s"
                  values="0.010;0.018;0.010"
                  repeatCount="indefinite"
                />
              </feTurbulence>
              {/* Static scale — never touched by JS at runtime. */}
              <feDisplacementMap in="SourceGraphic" in2="noise" scale={4} />
            </filter>
          </defs>

          <g filter={`url(#liquid-${uid})`}>
            <circle cx="60" cy="60" r="46" fill={`url(#foil-${uid})`} />
            <circle cx="60" cy="60" r="46" fill={`url(#sheen-${uid})`} />
          </g>

          {/* Rotating specular shimmer — CSS keyframe, compositor-only. R29 X4: the spin CADENCE
              carries the state — a gentle quicker spin when THINKING (on hold), a livelier sheen when
              SPEAKING, and the calm baseline otherwise. Duration only; still one keyframe, no per-frame
              JS. Reduced-motion zeroes it via the .orb-shimmer media rule in index.css. */}
          <g
            className="orb-shimmer"
            style={{
              transformOrigin: '60px 60px',
              animation: `orb-shimmer ${
                state === 'thinking' ? '5s' : state === 'speaking' ? '7s' : '9s'
              } linear infinite`,
            }}
          >
            <ellipse
              cx="60"
              cy="34"
              rx="20"
              ry="9"
              fill="rgba(255,255,255,0.28)"
              filter={`url(#liquid-${uid})`}
            />
          </g>
        </svg>
      </div>

      {/* Static soft glow — CSS only, no per-frame opacity/blur/transform writes. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle at 50% 48%, var(--c-accent), transparent 62%)',
          opacity: 0.35,
          filter: 'blur(10px)',
          transform: 'scale(0.9)',
          zIndex: -1,
        }}
      />

      <span className="sr-only" aria-live="polite">
        {ORB_STATE_LABEL[state]}
      </span>
    </div>
  );
}
